import { inject } from 'vue'

// Prescriptions are stored as attachments on the customer, written by
// CustomerAddPrescriptionDialog. Shared by the prescriptions drawer (full list) and the
// customer summary card (latest only) so both read the same records the same way.
export const PRESCRIPTION_REFERENCE_ORIGIN = 'prescription--data'

// Ordered by exam date, not created_at: a prescription entered today can carry an older date,
// so the most recently created record is not necessarily the current correction. created_at
// stays the tiebreak via the server sort, which a stable Array.sort preserves.
function prescriptionTime(attachment) {
  const time = new Date(attachment.metadata.prescription.date ?? attachment.created_at).getTime()
  return Number.isNaN(time) ? 0 : time
}

export function useCustomerPrescriptions() {
  const cl = inject('cl')

  // pageSize is left to the CL default unless a caller asks for a wider window — worth
  // passing when only the newest matters, since the server sorts on a different key than
  // the one we rank by.
  async function fetchCustomerPrescriptions(customerId, { pageSize } = {}) {
    const attachments = await cl.customers.attachments(customerId, {
      filters: { reference_origin_eq: PRESCRIPTION_REFERENCE_ORIGIN },
      sort: { created_at: 'desc' },
      fields: { attachments: ['reference', 'metadata', 'created_at'] },
      ...(pageSize ? { pageSize } : {})
    })

    // The SDK hands back an array carrying a meta property — copy to a plain array, and drop
    // anything without prescription metadata so consumers can read .prescription unguarded.
    return [...attachments]
      .filter((attachment) => attachment.metadata?.prescription)
      .sort((a, b) => prescriptionTime(b) - prescriptionTime(a))
  }

  async function fetchLatestCustomerPrescription(customerId, options) {
    const [latest] = await fetchCustomerPrescriptions(customerId, options)
    return latest ?? null
  }

  return {
    fetchCustomerPrescriptions,
    fetchLatestCustomerPrescription
  }
}
